import UploadZone from "@/components/UploadZone";
import { CheckCircle2, Euro, AlertTriangle, FileCheck2, FileText, ArrowRight, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth, ROLES } from "@/contexts/AuthContext";

const features = [
  { icon: FileText, title: "Sommario chiaro", desc: "Valore, durata, tipo di contratto e descrizione in linguaggio semplice.", color: "bg-slate-100 text-slate-900" },
  { icon: Euro, title: "Pagamenti e SAL", desc: "Ogni milestone con importo, condizioni e documenti richiesti.", color: "bg-emerald-100 text-emerald-700" },
  { icon: FileCheck2, title: "Documenti richiesti", desc: "Checklist DURC, POS, fideiussioni con quando servono.", color: "bg-blue-100 text-blue-700" },
  { icon: AlertTriangle, title: "Rischi con severità", desc: "Card colorate HIGH/MEDIUM/LOW con impatto economico e azione consigliata.", color: "bg-red-100 text-red-700" },
];

const ReadOnlyHero = ({ roleLabel, name }) => (
  <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20" data-testid="readonly-hero">
    <div className="text-center max-w-2xl mx-auto">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-full text-xs font-semibold uppercase tracking-wider text-slate-700 shadow-sm">
        <Lock size={12} />
        Modalità di sola lettura
      </div>
      <h1 className="mt-6 font-display text-4xl sm:text-5xl font-black tracking-tight leading-tight">
        {name ? <>Ciao <span className="text-emerald-600">{name.split(" ")[0]}</span>,</> : "Benvenuto,"}
        <br />questa è la tua vista.
      </h1>
      <p className="mt-4 text-slate-600 leading-relaxed">
        Hai effettuato l'accesso come <b>{roleLabel}</b>. Apri un contratto dai documenti per vedere
        ciò che ti riguarda.
      </p>
      <Link
        to="/documenti"
        data-testid="readonly-cta"
        className="mt-6 inline-flex items-center gap-2 bg-slate-900 text-white font-semibold px-5 py-2.5 rounded-md hover:bg-slate-800 transition-colors"
      >
        Vai ai contratti
        <ArrowRight size={16} />
      </Link>
    </div>
  </div>
);

const Landing = () => {
  const { user, caps, effectiveRole } = useAuth();

  if (!caps?.canUpload) {
    return <ReadOnlyHero roleLabel={ROLES[effectiveRole]?.label || "Ospite"} name={user?.name} />;
  }

  return (
    <div data-testid="landing-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 md:pt-20 md:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 animate-fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-full text-xs font-semibold uppercase tracking-wider text-slate-700 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                {user?.name ? `Ciao, ${user.name.split(" ")[0]}` : ROLES[effectiveRole]?.label}
              </div>

              <h1
                className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]"
                data-testid="hero-title"
              >
                <span className="text-brand-navy">Edilmind</span>
                <br />
                <span className="text-brand-orange">per il cantiere</span>
              </h1>

              <p className="mt-2 text-[11px] sm:text-xs font-bold tracking-[0.28em] text-brand-orange uppercase">
                Intelligenza · Ordine · Valore
              </p>

              <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-xl" data-testid="hero-subtitle">
                Carica un contratto edilizio e ricevi un'analisi chiara in pochi secondi.
                Sommario, pagamenti, documenti richiesti e rischi — in italiano, senza legalese.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  <span>Conforme D.Lgs. 36/2023</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  <span>Severità HIGH/MEDIUM/LOW</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircle2 size={16} className="text-emerald-600" />
                  <span>Powered by GPT-5.2</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 animate-fade-up" style={{ animationDelay: "0.1s" }}>
              <div className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-900/5 p-3 sm:p-4">
                <UploadZone />
              </div>
              <p className="text-center text-xs text-slate-400 mt-3 uppercase tracking-wider font-semibold">
                Modello: gpt-5.2
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">
              Cosa ottieni
            </span>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold tracking-tight">
              Il contratto, tradotto in azioni concrete
            </h2>
            <p className="mt-3 text-slate-600 text-lg">
              Niente riassunti generici. Solo dati specifici: numeri, percentuali, scadenze e azioni.
            </p>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f, i) => (
              <div
                key={f.title}
                data-testid={`feature-card-${i}`}
                className="group relative p-6 bg-white border border-slate-200 rounded-xl hover:border-slate-900 hover:-translate-y-0.5 transition-all"
              >
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${f.color}`}>
                  <f.icon size={20} />
                </div>
                <h3 className="mt-4 font-display font-bold text-lg">{f.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                <div className="mt-4 text-xs font-mono text-slate-300">0{i + 1}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <p>© {new Date().getFullYear()} Edilmind · Intelligenza. Ordine. Valore.</p>
          <p className="font-mono text-xs">Modello: gpt-5.2</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
